import { apiClient } from '../../../lib/api/client';
import type { AccountStatusValue } from '../../auth/api/auth';
import type { UserListItem } from '../../contacts/types';
import { getCustomerSegmentOptions } from '../../../lib/api/options';

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export type CreateUserRequest = {
  email: string;
  firstName: string;
  lastName: string;
  roleIds?: string[];
  customerSegmentIds?: string[];
};

export async function getCustomerSegmentsForSelect(): Promise<{ id: string; title: string }[]> {
  const segments = await getCustomerSegmentOptions({ activeOnly: true });
  return segments.map((s) => ({ id: s.id, title: s.title }));
}

export async function getRolesForSelect(): Promise<{ id: string; name: string }[]> {
  const response = await apiClient.get<{ id: string; name: string; permissionCount: number; userCount: number }[]>(
    '/roles'
  );
  return response.data.map((r) => ({ id: r.id, name: r.name }));
}

export async function getUsers(
  params: { pageNumber?: number; pageSize?: number } = {}
): Promise<PagedResult<UserListItem>> {
  const { pageNumber = 1, pageSize = 10 } = params;
  const searchParams = new URLSearchParams();
  searchParams.set('pageNumber', String(pageNumber));
  searchParams.set('pageSize', String(pageSize));
  const response = await apiClient.get<PagedResult<UserListItem>>(
    `/users/list?${searchParams.toString()}`
  );
  return response.data;
}

export type UpdateUserRequest = {
  firstName: string;
  lastName: string;
  email?: string;
  roleIds: string[];
  customerSegmentIds: string[];
};

export interface UserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  twoFactorEnabled: boolean;
  accountStatus?: AccountStatusValue;
  roleIds: string[];
  customerSegmentIds: string[];
  createdAtUtc: string;
  createdByUserId: string | null;
  createdByDisplayName: string | null;
  lastModifiedAtUtc: string | null;
  modifiedByUserId: string | null;
  modifiedByDisplayName: string | null;
}

export async function getUser(userId: string): Promise<UserDetail> {
  const response = await apiClient.get(`/users/${userId}`);
  return response.data;
}

export async function updateUser(userId: string, request: UpdateUserRequest): Promise<void> {
  await apiClient.put(`/users/${userId}`, request);
}

export async function createUser(request: CreateUserRequest): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>('/users', {
    ...request,
    roleIds: request.roleIds ?? [],
    customerSegmentIds: request.customerSegmentIds ?? [],
  });
  return response.data;
}

export async function revokeAllTokens(userId: string): Promise<void> {
  await apiClient.post(`/users/${userId}/revoke-all-tokens`);
}

export async function resendInvite(userId: string): Promise<void> {
  await apiClient.post(`/users/${userId}/resend-invite`);
}

export async function sendPasswordReset(userId: string): Promise<void> {
  await apiClient.post(`/users/${userId}/send-password-reset`);
}
